<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { localizedLabel, type Breakdown, type BreakdownSegment } from '@/utils/homebase';

// ONE stacked bar plus its legend — the whole of the homebase's charting (#403).
//
// NO CHART LIBRARY, deliberately. This is a single series of four to six values;
// ECharts would cost a lazy chunk and a canvas the design tokens cannot reach,
// and the AI usage card already proves the pattern for a series this small (its
// twelve-month history is plain divs). Plain elements also mean the segments are
// real buttons: a stage can be clicked through to the list it stands for, which
// a canvas rectangle cannot be.
//
// The colours come from `tokens/charts.css` and nowhere else: one lavender
// scale for every module's funnel (spec §2 data colors) — there is no
// per-module hue left to derive from, and no SECOND palette either: every
// segment sits on that one scale by its position in the bar. `tone` still rides
// along on the data (a module may want it later, and the legend LABEL already
// carries whatever meaning `success`/`warning`/… would have added), but it no
// longer selects a colour — a `warning`-toned segment next to a `success`-toned
// one used to read as two unrelated palettes in the same bar.

const props = defineProps<{ breakdown: Breakdown }>();
const emit = defineEmits<{ navigate: [deepLink: string] }>();

const { t, te } = useI18n();
const lbl = (item: { key: string; label: string }) => localizedLabel(item, t, te);

// Only the segments that actually hold something. A zero-width slice is
// invisible on the bar, and a legend that lists it makes an empty stage look
// like a live one — the module still SENDS its empty stages, because the shape
// of the funnel is its business and the drawing is ours.
const shown = computed(() => props.breakdown.segments.filter((s) => (s.value || 0) > 0));
const total = computed(() => shown.value.reduce((sum, s) => sum + (s.value || 0), 0));

// The lavender scale, spread across EVERY shown segment by its POSITION in the
// bar — not by value and not by `tone` (spec §2: one series, one scale; `tone`
// is data the legend label carries the meaning of, not a second palette).
//
// PALE → DEEP, first segment to last. A funnel's first stage is its widest, and
// putting the deepest step there would hand the largest area of the bar the most
// ink for the stage that matters least (everything still IN the funnel); the
// later, narrower stages carry the deepest ink instead, which is also what draws
// the eye to where the funnel has actually narrowed. A lone segment takes the
// full, deepest step.
function positionToken(index: number, count: number): string {
  if (count <= 1) return 'var(--scale-5)';
  const step = Math.min(5, Math.max(1, Math.round(1 + (index * 4) / (count - 1))));
  return `var(--scale-${step})`;
}

const colored = computed(() => {
  const count = shown.value.length;
  return shown.value.map((segment, index) => ({
    segment,
    color: positionToken(index, count),
    percent: total.value > 0 ? (segment.value / total.value) * 100 : 0,
  }));
});

function title(segment: BreakdownSegment): string {
  return `${lbl(segment)}: ${segment.value}`;
}

// The caption is the one label that carries a NUMBER inside the sentence, so it
// cannot go through `localizedLabel` — that resolves a label, it does not
// interpolate one. Same machine-key contract otherwise: a key the workspace
// knows is translated with `{ n }` filled in, and one it does not know falls
// back to the module's own words with the number in front of them.
const captionText = computed(() => {
  const caption = props.breakdown.caption;
  if (!caption || caption.value <= 0) return '';
  const path = `homebase.facts.${caption.key}`;
  return te(path) ? t(path, { n: caption.value }) : `${caption.value} ${caption.label}`;
});
</script>

<template>
  <section v-if="colored.length" class="breakdown" data-testid="breakdown-bar">
    <div class="breakdown-head">
      <h3 class="breakdown-title">{{ lbl(breakdown) }}</h3>
      <span class="breakdown-total">{{ total }}</span>
    </div>

    <!-- The bar. `role="img"` with one label rather than a list of buttons for a
         screen reader to walk: the segments below ARE the same data as links, so
         announcing them twice is noise. -->
    <div
      class="breakdown-track"
      role="img"
      :aria-label="colored.map(({ segment }) => title(segment)).join(', ')"
    >
      <component
        :is="entry.segment.deepLink ? 'button' : 'span'"
        v-for="entry in colored"
        :key="entry.segment.key"
        class="breakdown-slice"
        :class="{ 'breakdown-slice--link': !!entry.segment.deepLink }"
        :type="entry.segment.deepLink ? 'button' : undefined"
        :style="{ width: `${entry.percent}%`, backgroundColor: entry.color }"
        :title="title(entry.segment)"
        tabindex="-1"
        aria-hidden="true"
        @click="entry.segment.deepLink && emit('navigate', entry.segment.deepLink)"
      />
    </div>

    <!-- The legend carries the numbers and the links. It is the keyboard path to
         every stage, which is why the slices above are taken out of the tab
         order rather than duplicated into it. -->
    <ul class="breakdown-legend" role="list">
      <li v-for="entry in colored" :key="entry.segment.key">
        <component
          :is="entry.segment.deepLink ? 'button' : 'span'"
          class="breakdown-legend-item"
          :class="{ 'breakdown-legend-item--link': !!entry.segment.deepLink }"
          :type="entry.segment.deepLink ? 'button' : undefined"
          @click="entry.segment.deepLink && emit('navigate', entry.segment.deepLink)"
        >
          <span class="breakdown-dot" :style="{ backgroundColor: entry.color }" aria-hidden="true" />
          <span class="breakdown-legend-label">{{ lbl(entry.segment) }}</span>
          <span class="breakdown-legend-value">{{ entry.segment.value }}</span>
        </component>
      </li>
    </ul>

    <p v-if="captionText" class="breakdown-caption">{{ captionText }}</p>
  </section>
</template>

<style scoped>
.breakdown { display: flex; flex-direction: column; gap: 8px; }
.breakdown-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.breakdown-title {
  font-size: var(--text-caps);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.breakdown-total { font-size: var(--text-sm); font-weight: 600; color: var(--text-secondary); }

.breakdown-track {
  display: flex;
  width: 100%;
  height: 12px;
  gap: 2px;
  border-radius: 999px;
  overflow: hidden;
  /* Darker than `--scale-1`, so even the palest step of the ramp still reads as
     a filled slice rather than as more track. */
  background: var(--surface-wash);
}
.breakdown-slice { display: block; height: 100%; border: 0; padding: 0; min-width: 3px; }
.breakdown-slice--link { cursor: pointer; }

.breakdown-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.breakdown-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: none;
  font-size: var(--text-xs);
  color: var(--text-secondary);
}
.breakdown-legend-item--link { cursor: pointer; }
.breakdown-legend-item--link:hover { color: var(--text-heading); }
.breakdown-legend-item--link:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
  border-radius: 4px;
}
.breakdown-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  flex: none;
  /* A hairline so the ramp's palest steps are still visible as dots on the
     card's own white. */
  outline: 1px solid var(--border-default);
  outline-offset: -1px;
}
.breakdown-legend-label { white-space: nowrap; }
.breakdown-legend-value { font-weight: 600; color: var(--text-heading); }

.breakdown-caption { margin: 0; font-size: var(--text-xs); color: var(--text-tertiary); }
</style>
